import { Component, computed, OnInit, Renderer2 } from '@angular/core';
import { BreadcrumbService } from './core/services/breadcrumb/breadcrumb.service';
import { NzResizeEvent } from 'ng-zorro-antd/resizable';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MenuInterface } from './core/interfaces/user.interface';
import { CurrentUserService } from './core/services/Current-user/currentuser.service';
import { NavigationEnd, Router } from '@angular/router';
import { ThemeService } from './core/services/theme.service';
import { filter, Subscription } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../environments/environment';

declare var _paq: any[];

/** Bump manually on each deploy to verify which build is live. */
const APP_VERSION = '2.0.3';

interface AdminMenuItem {
  permission: string;
  link: string;
  icon: string;
  label: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  public loading: boolean = false;
  isDarkMode = false;
  isCollapsed = false;
  siderWidth = 256;
  id = -1;
  currentUser: any;
  breadcrumbs = computed(() => this.breadcrumbService.breadcrumbs());
  menuItems: MenuInterface[] = [];
  isDropdownOpen = false;
  readonly appVersion = APP_VERSION;

  /** Entries of the Administration menu, each with the permission that reveals it. */
  readonly adminMenuItems: AdminMenuItem[] = [
    {
      permission: 'Administration.Databases',
      link: '/database',
      icon: 'database',
      label: 'Databases',
    },
    { permission: 'Administration.Roles', link: '/role', icon: 'team', label: 'Roles' },
    {
      permission: 'Administration.TableCategories',
      link: '/table-category',
      icon: 'folder',
      label: 'Table Categories',
    },
    { permission: 'Administration.Tables', link: '/table', icon: 'table', label: 'Tables' },
    {
      permission: 'Administration.Users',
      link: '/table-group',
      icon: 'appstore',
      label: 'Table Groups',
    },
    { permission: 'Administration.Users', link: '/user', icon: 'user', label: 'Users' },
  ];

  private adminMenuSource: { [key: string]: boolean } | null = null;
  private cachedAdminMenuItems: AdminMenuItem[] = [];

  /**
   * Only the entries the user may actually open. The parent menu is driven by this list, so a
   * granted `Administration.*` permission without a menu entry of its own (`Administration.
   * Tables.Create` for instance) can no longer surface an empty Administration menu.
   */
  get visibleAdminMenuItems(): AdminMenuItem[] {
    const permissions = this.currentUser?.adminMenu ?? null;

    // the whole map is replaced when the permissions load, so comparing the reference is enough
    if (permissions !== this.adminMenuSource) {
      this.adminMenuSource = permissions;
      this.cachedAdminMenuItems = this.adminMenuItems.filter(
        (item) => permissions?.[item.permission] === true,
      );
    }

    return this.cachedAdminMenuItems;
  }

  hasAnyAdminPermission(): boolean {
    return this.visibleAdminMenuItems.length > 0;
  }

  private themeLinkElement: HTMLLinkElement | any = null;
  darkThemeHref = '/assets/ng-zorro-antd/ng-zorro-antd.dark.css';
  private subscription: Subscription;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private oidcSecurityService: OidcSecurityService,
    private currentUserService: CurrentUserService,
    private router: Router,
    private themeService: ThemeService,
    private renderer: Renderer2,
    private notification: NzNotificationService,
  ) {
    this.isDarkMode = this.themeService['darkMode'].getValue();
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.user;

    this.initMatomo();

    // Track SPA navigations in Matomo
    this.trackPageViews();

    this.subscription = this.themeService.isDarkMode$.subscribe((isDark) => {
      this.isDarkMode = isDark;
      this.handleThemeAssets(isDark);
    });
  }

  initMatomo() {
    if (!environment.matomoUrl) {
      return;
    }

    window._paq = window._paq || [];
    _paq.push(['enableLinkTracking']);

    const u = environment.matomoUrl;
    _paq.push(['setTrackerUrl', u + 'matomo.php']);
    _paq.push(['setSiteId', environment.matomoSiteId ?? '1']);

    const script = document.createElement('script');
    const firstScript = document.getElementsByTagName('script')[0];
    script.async = true;
    script.src = u + 'matomo.js';
    firstScript.parentNode?.insertBefore(script, firstScript);
  }

  trackPageViews() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (typeof _paq === 'undefined') {
          return;
        }
        const userId = this.currentUserService.user?.username || this.currentUserService.user?.id;
        if (userId) {
          _paq.push(['setUserId', userId]);
        }
        _paq.push(['setCustomUrl', event.urlAfterRedirects]);
        _paq.push(['setDocumentTitle', document.title]);
        _paq.push(['trackPageView']);
      });
  }

  private handleThemeAssets(isDark: boolean) {
    if (isDark) {
      if (!this.themeLinkElement) {
        this.themeLinkElement = this.renderer.createElement('link');
        this.themeLinkElement.rel = 'stylesheet';
        this.themeLinkElement.href = this.darkThemeHref;
        this.renderer.appendChild(document.head, this.themeLinkElement);
      }
    } else {
      if (this.themeLinkElement) {
        this.renderer.removeChild(document.head, this.themeLinkElement);
        this.themeLinkElement = null;
      }
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onSideResize({ width }: NzResizeEvent): void {
    cancelAnimationFrame(this.id);
    this.id = requestAnimationFrame(() => {
      this.siderWidth = width!;
    });
  }

  logout(e: any): void {
    e.preventDefault();
    if (typeof _paq !== 'undefined') {
      _paq.push(['resetUserId']);
      _paq.push(['trackPageView']);
    }
    this.oidcSecurityService.logoff().subscribe((result) => {});
  }

  edit(): void {
    console.log('Editing profile...');
  }

  reports() {
    this.loading = true;

    // Navigate with error handling
    this.router
      .navigate(['/my-reports'])
      .then(() => {
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        // Display error notification
        this.notification.error('', error.message || '', { nzDuration: 5000 });
        console.error('Navigation error:', error);
      });
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }
  hoveringSidebar = false;

  get effectiveCollapsed(): boolean {
    return this.isCollapsed && !this.hoveringSidebar;
  }

  onMouseEnterSidebar(): void {
    if (this.isCollapsed) {
      this.hoveringSidebar = true;
    }
  }

  onMouseLeaveSidebar(): void {
    this.hoveringSidebar = false;
  }

  get logoPath(): string {
    return this.isDarkMode ? '/osstools-logo-white.svg' : '/osstools-logo-light.svg';
  }

  get collapsedLogoPath(): string {
    return this.isDarkMode ? '/osstools-mark.svg' : '/osstools-mark-light.svg';
  }

  get expandedLogoStyle(): string {
    return 'width: 175px; height: auto; max-height: 48px; object-fit: contain';
  }

  get collapsedLogoStyle(): string {
    return 'width: 40px; height: 40px; object-fit: contain';
  }
}
